import { useEffect } from 'react';
import { X } from 'lucide-react';
import type { EmailLog } from '../../../lib/emailsApi';

function formatDateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-gray-900">{value}</dd>
    </div>
  );
}

/**
 * Read-only detail view for a single EmailLog row (CLE-310). Surfaces the full
 * audit record — recipient, subject, template, status, send + open timestamps,
 * tracking id and any captured metadata.
 *
 * Dismissible via the ✕ button, Escape, or a click on the backdrop.
 */
export function EmailDetailModal({ email, onClose }: { email: EmailLog; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const statusClass =
    email.status === 'SENT' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Email detail</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        <dl className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Subject" value={email.subject} />
          </div>
          <Field label="Recipient" value={email.to} />
          <Field label="Template" value={email.template} />
          <Field
            label="Status"
            value={
              <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusClass}`}>
                {email.status}
              </span>
            }
          />
          <Field label="Open count" value={email.openCount} />
          <Field label="Sent at" value={formatDateTime(email.sentAt)} />
          <Field label="First opened" value={formatDateTime(email.firstOpenedAt)} />
          <Field label="Last opened" value={formatDateTime(email.lastOpenedAt)} />
          <div className="sm:col-span-2">
            <Field
              label="Tracking ID"
              value={<span className="font-mono text-xs">{email.trackingId}</span>}
            />
          </div>
          {email.metadata && Object.keys(email.metadata).length > 0 && (
            <div className="sm:col-span-2">
              <Field
                label="Metadata"
                value={
                  <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-700">
                    {JSON.stringify(email.metadata, null, 2)}
                  </pre>
                }
              />
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}
